import { PRICING } from '@/lib/pricing'
import { donationMultiplier } from '@/lib/feature-flags'

/** Subscription go-live: before this date, everyone has full access. On/after this date, trial/subscriber logic applies. */
export const SUBSCRIPTION_GO_LIVE_DATE = new Date(PRICING.freeUntilISO + 'T00:00:00.000Z')

/** Emails that always have full access (support/manual grants). Set ACCESS_GRANT_EMAILS env (comma-separated) or use defaults. */
const DEFAULT_GRANT_ACCESS_EMAILS = ['jleneus@gmail.com']

export function getGrantAccessEmails(): Set<string> {
  const raw = process.env.ACCESS_GRANT_EMAILS
  if (raw && raw.trim()) {
    const list = raw.split(',').map((e) => e.trim().toLowerCase()).filter(Boolean)
    return new Set(list)
  }
  return new Set(DEFAULT_GRANT_ACCESS_EMAILS.map((e) => e.toLowerCase()))
}

/** True if this email is explicitly granted full access (bypasses subscription/trial checks). */
export function hasGrantedAccess(email: string | null | undefined): boolean {
  if (!email) return false
  return getGrantAccessEmails().has(email.trim().toLowerCase())
}

/** Free tier: minutes of premium access per calendar day (UTC). */
export const FREE_TIER_DAILY_MINUTES = 15
const FREE_TIER_DAILY_MS = FREE_TIER_DAILY_MINUTES * 60 * 1000

export interface FreeTierDailyRow {
  freeTierUsageDate?: string | Date | null
  freeTierAllowUntil?: string | Date | null
}

export interface FreeTierDailyAccess {
  hasAccess: boolean
  /** When access expires (ISO string); set when within the 15-min window. */
  allowUntil?: string | null
  /** Set when we started the window today; caller should persist to DB. */
  usageDate?: string | null
}

/**
 * For free_tier users only: grant 15 minutes of access per calendar day (UTC).
 * Returns hasAccess and optional allowUntil/usageDate. When usageDate is set, caller must update DB.
 */
export function getFreeTierDailyAccess(
  user: FreeTierDailyRow,
  now: Date = new Date(),
  // Window length in ms. Defaults to the standard 15 min; the paywall friction experiment
  // (paywallExperiment.ts) passes a shorter window for the warm segment's treatment arm.
  dailyMs: number = FREE_TIER_DAILY_MS
): FreeTierDailyAccess {
  const today = now.toISOString().slice(0, 10) // YYYY-MM-DD
  const usageDate = user.freeTierUsageDate
    ? typeof user.freeTierUsageDate === 'string'
      ? user.freeTierUsageDate.slice(0, 10)
      : (user.freeTierUsageDate as Date).toISOString().slice(0, 10)
    : null
  const allowUntil = user.freeTierAllowUntil
    ? typeof user.freeTierAllowUntil === 'string'
      ? user.freeTierAllowUntil
      : (user.freeTierAllowUntil as Date).toISOString()
    : null

  // First use today (or never used): start 15-min window
  if (!usageDate || usageDate < today) {
    const until = new Date(now.getTime() + dailyMs)
    return { hasAccess: true, allowUntil: until.toISOString(), usageDate: today }
  }

  // Same day: already used today
  if (usageDate === today) {
    if (allowUntil && now < new Date(allowUntil)) {
      return { hasAccess: true, allowUntil }
    }
    return { hasAccess: false }
  }

  // usageDate > today (shouldn't happen): treat as new day.
  // ⚠ USES dailyMs, NOT THE CONSTANT. This branch started life hardcoded to FREE_TIER_DAILY_MS, which
  // silently handed a treatment-arm user the full 15 minutes and made the experiment under-report its
  // own effect. And it is NOT unreachable: the day key here is toISOString().slice(0,10), i.e. UTC,
  // while this codebase's trading day rolls at 4AM ET, so a future-dated usageDate is reachable around
  // the UTC boundary. Any window this function opens must honour the caller's window length.
  const until = new Date(now.getTime() + dailyMs)
  return { hasAccess: true, allowUntil: until.toISOString(), usageDate: today }
}

export type AccessLevel = 'free_tier' | 'free_trial' | 'subscriber'

/**
 * Inputs to the UNIFIED access decision. These are the ONLY signals that determine whether a user
 * has premium access — derived identically by BOTH the server gate (`getSessionWithAccess` in
 * require-access.ts) and the client-facing status endpoint (subscription-status route). The values
 * all come from the premium-days BALANCE engine + feature flags; no date-based trial math here.
 */
export interface AccessDecisionInput {
  /** Active paid subscriber (users.isSubscriber). */
  isSubscriber: boolean
  /** Premium-days balance BEFORE today's on-access burn (users.premiumDaysRemaining). Gate on the
   *  pre-burn balance so a user who spends their last day today still keeps access for today. */
  premiumDaysBalance: number
  /** Explicit support/manual grant (hasGrantedAccess(email)). Always full access. */
  granted: boolean
  /** SUBSCRIPTION_GATING_ENABLED. When false, everyone has access (the launch off-switch). */
  gatingEnabled: boolean
  /** Free-tier daily 15-min window result (only consulted when balance/subscriber/grant deny). */
  freeTier?: FreeTierDailyAccess
}

export interface AccessDecision {
  hasAccess: boolean
  accessLevel: AccessLevel
  freeTierAllowUntil: string | null | undefined
  freeTierLimitReached: boolean
}

/**
 * THE single source of truth for "does this user have access right now". Pure (no DB / no clock).
 * Both the client status endpoint and the server premium gate call this with the SAME balance-engine
 * inputs, so they can never disagree. Order of precedence:
 *   1. gating OFF  → everyone has access (fast-path off-switch, both sides)
 *   2. explicit grant → access (level reflects subscriber/balance for display)
 *   3. subscriber → access
 *   4. premium-days balance > 0 → access (trial + donation + sub all live in this one balance)
 *   5. otherwise → free_tier; access only inside today's 15-min window (freeTier.hasAccess)
 */
export function decideAccess(input: AccessDecisionInput): AccessDecision {
  const { isSubscriber, premiumDaysBalance, granted, gatingEnabled, freeTier } = input

  const accessLevel: AccessLevel = isSubscriber
    ? 'subscriber'
    : premiumDaysBalance > 0
      ? 'free_trial'
      : 'free_tier'

  // Off-switch fast-path: gating disabled → everyone has access (kept identical on both sides).
  if (!gatingEnabled) {
    return { hasAccess: true, accessLevel, freeTierAllowUntil: undefined, freeTierLimitReached: false }
  }

  // Balance / subscriber / explicit grant all grant full access.
  if (granted || isSubscriber || premiumDaysBalance > 0) {
    return { hasAccess: true, accessLevel, freeTierAllowUntil: undefined, freeTierLimitReached: false }
  }

  // No balance → free tier. Access only inside today's 15-min window.
  const hasAccess = freeTier?.hasAccess === true
  return {
    hasAccess,
    accessLevel: 'free_tier',
    freeTierAllowUntil: freeTier?.allowUntil ?? null,
    freeTierLimitReached: !hasAccess,
  }
}

export interface UserSubscription {
  isSubscriber: boolean
  trialStart?: string | null
  trialEnd?: string | null
  subscriptionStatus?: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid'
  /** Total donated $ for days-based access */
  totalDonated?: number
}

/**
 * Resolve user's access level (free_tier, free_trial, subscriber).
 * Before go-live date, everyone is treated as having trial-level access for display; hasAccess still returns true.
 * Donation-earned days count as subscriber-level access when days > 0.
 */
export function getAccessLevel(user: UserSubscription): AccessLevel {
  const now = new Date()
  if (now < SUBSCRIPTION_GO_LIVE_DATE) {
    return 'free_trial' // pre go-live: full access, show as trial for consistency
  }
  if (user.isSubscriber) return 'subscriber'
  if (user.trialStart && user.trialEnd && isWithinTrialPeriod(user.trialEnd)) return 'free_trial'
  if (getSubscriptionDaysEarned(user.totalDonated ?? 0) > 0) return 'subscriber' // donation days = active
  return 'free_tier'
}

/**
 * Compute premium days earned from donations.
 * Formula: floor((totalDonated / daily_rate) × live donationMultiplier()).
 * The multiplier is DATE-GATED (2× pre-launch, 1× at/after go-live) unless the owner sets an
 * explicit `DONATION_MULTIPLIER` env override — evaluated live per call, never stale.
 */
export function getSubscriptionDaysEarned(totalDonated: number): number {
  if (totalDonated <= 0) return 0
  const baseDays = totalDonated / PRICING.donationDailyRate  // DECOUPLED rate ($2.50/day), not the $0.95 sub rate
  return Math.floor(baseDays * donationMultiplier())
}

/**
 * Check if user has access to premium content (full site).
 * Before go-live: true. On/after go-live: true if subscriber, within trial, or has donation-earned days.
 */
export function hasAccess(user: UserSubscription): boolean {
  const now = new Date()
  if (now < SUBSCRIPTION_GO_LIVE_DATE) return true
  if (user.isSubscriber) return true
  if (user.trialEnd && isWithinTrialPeriod(user.trialEnd)) return true
  // Donation-earned days: active as long as they have premium days
  const daysEarned = getSubscriptionDaysEarned(user.totalDonated ?? 0)
  if (daysEarned > 0) return true
  return false
}

/**
 * Check if user is within trial period
 * @param trialEnd - Trial end date string
 * @returns boolean - true if still within trial
 */
export function isWithinTrialPeriod(trialEnd: string): boolean {
  const now = new Date()
  const trialEndDate = new Date(trialEnd)
  return now < trialEndDate
}

/**
 * Get user's subscription status for display
 * @param user - User subscription data
 * @returns string - Status to display
 */
export function getSubscriptionStatus(user: UserSubscription): string {
  if (user.isSubscriber) {
    return user.subscriptionStatus === 'active' ? 'Premium' : 'Free Trial'
  }
  
  if (user.trialEnd && isWithinTrialPeriod(user.trialEnd)) {
    return 'Free Trial'
  }
  
  return 'Subscription Required'
}

/**
 * Get days remaining in trial
 * @param trialEnd - Trial end date string
 * @returns number - Days remaining (0 if expired)
 */
export function getTrialDaysRemaining(trialEnd: string): number {
  const now = new Date()
  const trialEndDate = new Date(trialEnd)
  const diffTime = trialEndDate.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return Math.max(0, diffDays)
}
